import { parcelPhotoIdFormat } from "@redotech/redo-ids/ids";
import { ParcelPhotoSource } from "@redotech/redo-model/fulfillments/parcel-photo";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Single-shot server-side upload. The browser POSTs the JPEG blob as
 * a multipart field and the server takes care of writing to S3 +
 * inserting the CRDB row + firing the timeline event. Picked over a
 * presigned-PUT design because:
 *
 *   - Bucket CORS isn't configured for browser-direct uploads, and
 *     "request infra to ship a CORS rule" is friction we'd rather not
 *     pay for ~300KB JPEGs capped at 3-per-FG.
 *   - One round trip end-to-end (no `createUploadSession` +
 *     `finalize` orchestration), so client and server errors are way
 *     easier to debug ("HTTP 500" tells the whole story).
 *   - Server has full visibility — easy to add format validation,
 *     virus scanning, EXIF stripping, etc. without coordinating
 *     browser changes.
 *
 * Pass the JPEG blob in `options.files: [blob]` on the client side;
 * the handler receives it as `files[0]` (an `Express.Multer.File`).
 */
export const uploadParcelPhoto = {
  name: "uploadParcelPhoto" as const,
  input: z.object({
    fulfillmentGroupId: zExt.objectId(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    /**
     * Client-side capture timestamp. Persisted on the row so the
     * timeline can show "captured at" rather than "uploaded at,"
     * which is what the operator actually cares about.
     */
    capturedAt: z.coerce.date(),
    source: createEnum(ParcelPhotoSource, { enumName: "ParcelPhotoSource" }),
  }),
  output: z.object({
    /** Crockford32-prefixed CRDB id for the persisted photo row. */
    photoId: parcelPhotoIdFormat.schema,
    /** Public URL of the photo bytes, safe to render in `<img src>`. */
    url: z.string(),
    /** Server-side commit timestamp; matches the timeline event. */
    confirmedAt: z.coerce.date(),
  }),
};
